import { MOCK_SCORE_CONFIG } from 'src/apps/dashboard/constants/BusinessUnitsTable';
import { IBusinessUnitRow } from 'src/apps/dashboard/types/BusinessUnitsTableTypes';
import { BusinessUnit } from 'src/apps/dashboard/types/DashboardTypes';

import { BusinessUnitsTableDataQuery } from '../BusinessUnitsTable.generated';

import { generateMockScore } from './mockScoreUtils';

export interface BusinessUnitsTableResult {
	rows: IBusinessUnitRow[];
	total: number;
	unfilteredTotal: number;
}

interface IssueResult {
	filtered_custom_fields?: Array<string | null> | null;
	issue_id_count?: number;
	critical_issues_sum?: number;
	serious_issues_sum?: number;
}

const processSumResults = (
	issueResults: IssueResult[] | null | undefined,
	criticalIssuesMap: Map<string, number>,
	seriousIssuesMap: Map<string, number>,
	filteredUnitIds?: Set<string>
): void => {
	if (!issueResults) {
		return;
	}

	issueResults.forEach((item) => {
		const criticalSum = item.critical_issues_sum ?? 0;
		const seriousSum = item.serious_issues_sum ?? 0;

		// If there's a filter applied, only process unitIds that match the filter
		if (filteredUnitIds && filteredUnitIds.size > 0) {
			const validUnitIds =
				item.filtered_custom_fields?.filter(
					(field): field is string =>
						field !== null && field !== undefined && filteredUnitIds.has(field)
				) ?? [];

			// Process each valid unitId
			validUnitIds.forEach((unitId) => {
				const existingCritical = criticalIssuesMap.get(unitId) ?? 0;
				const existingSerious = seriousIssuesMap.get(unitId) ?? 0;

				criticalIssuesMap.set(unitId, existingCritical + criticalSum);
				seriousIssuesMap.set(unitId, existingSerious + seriousSum);
			});
		} else {
			// No filter: use original behavior - take the first unitId
			const unitId = item.filtered_custom_fields?.[0];
			if (unitId) {
				const existingCritical = criticalIssuesMap.get(unitId) ?? 0;
				const existingSerious = seriousIssuesMap.get(unitId) ?? 0;

				criticalIssuesMap.set(unitId, existingCritical + criticalSum);
				seriousIssuesMap.set(unitId, existingSerious + seriousSum);
			}
		}
	});
};

const createBusinessUnitRow = (
	unit: BusinessUnit,
	criticalIssues: number,
	seriousIssues: number
): IBusinessUnitRow => {
	// TODO: Replace with actual unique issues count after deduplication is implemented on backend
	const uniqueIssues = criticalIssues + seriousIssues;

	const mockScores = {
		webFlowAnalyzerScore: generateMockScore(
			unit.id,
			MOCK_SCORE_CONFIG.WEB_FLOW_ANALYZER_BASE_SCORE
		),
		webSdksScore: generateMockScore(unit.id, MOCK_SCORE_CONFIG.WEB_SDK_BASE_SCORE)
	};

	return {
		id: unit.id,
		name: unit.name,
		criticalIssues,
		seriousIssues,
		uniqueIssues,
		webFlowAnalyzerScore: mockScores.webFlowAnalyzerScore,
		webSdksScore: mockScores.webSdksScore,
		// Add flat numeric fields for sorting
		webFlowAnalyzerScoreValue: mockScores.webFlowAnalyzerScore.value,
		webSdksScoreValue: mockScores.webSdksScore.value
	};
};

export const transformBusinessUnitsTableResults = (
	rawResults: BusinessUnitsTableDataQuery,
	businessUnits: BusinessUnit[],
	filteredUnitIds?: Set<string>
): BusinessUnitsTableResult => {
	const criticalIssuesMap = new Map<string, number>();
	const seriousIssuesMap = new Map<string, number>();

	// Process Web SDK issues (both critical and serious)
	// Only filter unitIds if a filter is explicitly applied
	processSumResults(
		rawResults?.wsdkIssuesCount,
		criticalIssuesMap,
		seriousIssuesMap,
		filteredUnitIds
	);
	// Process WFA issues (both critical and serious)
	processSumResults(
		rawResults?.wfaIssuesCount,
		criticalIssuesMap,
		seriousIssuesMap,
		filteredUnitIds
	);

	const unitsWithData = new Set<string>();
	[...criticalIssuesMap.keys(), ...seriousIssuesMap.keys()].forEach((unitId) => {
		unitsWithData.add(unitId);
	});

	const rows: IBusinessUnitRow[] = businessUnits
		.filter((unit) => unitsWithData.has(unit.id))
		.map((unit) => {
			const criticalIssues = criticalIssuesMap.get(unit.id) ?? 0;
			const seriousIssues = seriousIssuesMap.get(unit.id) ?? 0;

			return createBusinessUnitRow(unit, criticalIssues, seriousIssues);
		});

	return {
		rows,
		total: rows.length,
		unfilteredTotal: rows.length
	};
};
